import { theme } from 'styles/theme/theme';
import type { GoalScorerEventKind, PlaceholderOverflowMode, PlaceholderTextAlign, PlaceholderVerticalAlign } from './types';

/**
 * Layout of the `goal_scorers` list — one line per scorer, an optional
 * «Пен.» block for the shootout under it — inside the placeholder box.
 *
 * A pure function with an injected `measure`, so the editor preview and the
 * tests share it without Fabric. The render service (IMGM-1501) repeats the
 * same algorithm on its own side: change it here → update the description in
 * `.claude/docs/media-manager-server-render.md`.
 */

/** `spacer` — an empty row `alignShootout` pads a list with; laid out, never drawn. */
export type GoalScorerLineKind = 'goal' | 'ownGoal' | 'shootoutHeader' | 'shootout' | 'more' | 'spacer';

export interface GoalScorerLine {
  text: string;
  kind: GoalScorerLineKind;
}

export interface PlacedGoalScorerLine extends GoalScorerLine {
  /** Left edge, box-relative (0 = box left). */
  x: number;
  /** Top edge, box-relative (0 = box top). */
  y: number;
  width: number;
  height: number;
}

export interface GoalScorersLayout {
  fontSize: number;
  columns: 1 | 2;
  placed: PlacedGoalScorerLine[];
  /** Scorer lines (headers excluded) that did not fit and are summed in «+N». */
  hiddenCount: number;
}

export type MeasureText = (text: string, fontSize: number) => { width: number; height: number };

export interface GoalScorersLayoutOptions {
  minFontSize: number;
  maxFontSize: number;
  textAlign: PlaceholderTextAlign;
  verticalAlign?: PlaceholderVerticalAlign;
  overflowMode?: PlaceholderOverflowMode;
  /**
   * Rows the goals block and the kicks block are padded to, so a list with
   * fewer goals still puts its «Пен.» on the same row as the other team's.
   * Applied only when the padded list fits one column whole; otherwise the
   * list is laid out as it is.
   */
  alignShootout?: ShootoutRows;
}

/** Goal rows above the «Пен.» header and kick rows below it. */
export interface ShootoutRows {
  goalRows: number;
  kickRows: number;
}

export interface GoalScorersSampleOptions {
  ownGoalMark?: boolean;
  hiddenEventKinds?: readonly GoalScorerEventKind[];
}

export const SHOOTOUT_HEADER = 'Пен.';
export const OWN_GOAL_MARK = '(АГ)';
export const DEFAULT_OWN_GOAL_FILL = theme.colors.error;
/** Gap between the two columns, in ems of the current font size. */
export const GOAL_SCORERS_COLUMN_GAP_EM = 1;
/**
 * Space above the «Пен.» header, in line heights: without it the shootout
 * block reads as more goal lines. Only between the goals and the header — a
 * header at the top of a column gets none.
 */
export const GOAL_SCORERS_SHOOTOUT_GAP = 0.5;

/**
 * Editor-only sample markup: `АГ ` — own goal, `П ` — in-match penalty, a
 * `Пен.` line opens the shootout block. The render service never reads it —
 * it builds the lines from the match's goal events.
 */
export const GOAL_SCORERS_SAMPLE = [
  "Нигматулин Р. 16', 39'",
  "П Чуриков Д. 15'",
  "АГ Павлов К. 34'",
  SHOOTOUT_HEADER,
  'Нигматулин Р.',
  'Чуриков Д.',
].join('\n');

const OWN_GOAL_PREFIX = 'АГ ';
const PENALTY_PREFIX = 'П ';
const FONT_PRECISION = 0.25;

export function parseGoalScorersSample(text: string, options: GoalScorersSampleOptions = {}): GoalScorerLine[] {
  const hidden = new Set(options.hiddenEventKinds ?? []);
  const withMark = options.ownGoalMark ?? true;
  const goals: GoalScorerLine[] = [];
  const shootout: GoalScorerLine[] = [];
  let inShootout = false;

  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    if (line.toLowerCase() === SHOOTOUT_HEADER.toLowerCase()) {
      inShootout = true;
      continue;
    }
    if (inShootout) {
      if (!hidden.has('shootout')) shootout.push({ text: line, kind: 'shootout' });
      continue;
    }
    if (line.startsWith(OWN_GOAL_PREFIX)) {
      if (hidden.has('ownGoal')) continue;
      const name = line.slice(OWN_GOAL_PREFIX.length).trim();
      goals.push({ text: withMark ? `${name} ${OWN_GOAL_MARK}` : name, kind: 'ownGoal' });
      continue;
    }
    if (line.startsWith(PENALTY_PREFIX)) {
      if (hidden.has('penalty')) continue;
      goals.push({ text: line.slice(PENALTY_PREFIX.length).trim(), kind: 'goal' });
      continue;
    }
    if (hidden.has('goal')) continue;
    goals.push({ text: line, kind: 'goal' });
  }

  if (shootout.length === 0) return goals;
  return [...goals, { text: SHOOTOUT_HEADER, kind: 'shootoutHeader' }, ...shootout];
}

/**
 * Algorithm:
 * 1. Base font `fsBase` — the largest size in [min, max] at which the widest
 *    line fits the box width and one line fits its height.
 * 2. One column at `fsBase` fits → done. Otherwise, by `overflowMode`:
 *    - `shrink`: the largest font ≥ min at which one column fits; none → cut at min;
 *    - `columns`: two columns at the largest font ≤ fsBase whose widest line
 *      fits a column; the rows still overflow → cut;
 *    - `truncate`: cut at `fsBase`;
 *    - `auto` (default): `shrink` without the cut; at min still too tall →
 *      two columns at min (if lines fit a column) → cut.
 * 3. Cut = the first `capacity − 1` lines plus a «+N» line, N = hidden scorer
 *    lines (the «Пен.» header is not counted). A header left last is dropped.
 *    Two columns: capacity = 2 × rows that fit; the left column gets ⌈n/2⌉ lines.
 * 4. Line height = the tallest measured line; lines are stacked without a gap,
 *    except `GOAL_SCORERS_SHOOTOUT_GAP` above a «Пен.» header that is not at
 *    the top of its column — counted wherever a height is (fit, cut, anchor);
 *    the block is anchored by `verticalAlign` (default `center`), each line by
 *    `textAlign` within its column.
 * 5. `alignShootout`: the goals block and the kicks block are padded with
 *    empty `spacer` rows to the given counts and the padded list is laid out
 *    by steps 1–4. Kept only if it comes out as one column with every row
 *    shown — padding must never push a real scorer into «+N» or a second
 *    column; otherwise the list is laid out unpadded. Spacers are never drawn.
 */
export function layoutGoalScorers(
  lines: readonly GoalScorerLine[],
  box: { width: number; height: number },
  options: GoalScorersLayoutOptions,
  measure: MeasureText,
): GoalScorersLayout {
  if (options.alignShootout) {
    const padded = padShootout(lines, options.alignShootout);
    if (padded.length !== lines.length) {
      const layout = layoutLines(padded, box, options, measure);
      if (layout.columns === 1 && layout.placed.length === padded.length) return layout;
    }
  }
  return layoutLines(lines, box, options, measure);
}

/** Pads the goals above the «Пен.» header and the kicks below it with spacer rows. A list without one is returned as is. */
export function padShootout(lines: readonly GoalScorerLine[], rows: ShootoutRows): GoalScorerLine[] {
  const header = lines.findIndex((l) => l.kind === 'shootoutHeader');
  if (header < 0) return [...lines];
  const goals = lines.slice(0, header);
  const kicks = lines.slice(header + 1);
  const spacers = (n: number): GoalScorerLine[] => Array.from({ length: Math.max(0, n) }, () => ({ text: '', kind: 'spacer' }));
  return [...goals, ...spacers(rows.goalRows - goals.length), ...lines.slice(header, header + 1), ...kicks, ...spacers(rows.kickRows - kicks.length)];
}

function layoutLines(
  lines: readonly GoalScorerLine[],
  box: { width: number; height: number },
  options: GoalScorersLayoutOptions,
  measure: MeasureText,
): GoalScorersLayout {
  const { minFontSize, maxFontSize } = options;
  const mode = options.overflowMode ?? 'auto';
  if (lines.length === 0 || box.width <= 0 || box.height <= 0) {
    return { fontSize: maxFontSize, columns: 1, placed: [], hiddenCount: 0 };
  }

  const texts = lines.map((l) => l.text);
  const widest = (fs: number) => Math.max(...texts.map((t) => measure(t, fs).width));
  const lineHeight = (fs: number) => Math.max(...texts.map((t) => measure(t, fs).height));
  const columnWidth = (fs: number) => (box.width - fs * GOAL_SCORERS_COLUMN_GAP_EM) / 2;
  const fitsOneColumn = (fs: number) => widest(fs) <= box.width && columnHeight(lines, lineHeight(fs)) <= box.height;
  const fitsColumnWidth = (fs: number) => widest(fs) <= columnWidth(fs);

  const fsBase = largestFontSize(minFontSize, maxFontSize, (fs) => widest(fs) <= box.width && lineHeight(fs) <= box.height);
  const build = (fs: number, columns: 1 | 2) => placeLines(lines, box, options, measure, fs, columns);

  if (fitsOneColumn(fsBase)) return build(fsBase, 1);

  const twoColumnFont = (upper: number): number | undefined =>
    fitsColumnWidth(minFontSize) ? largestFontSize(minFontSize, upper, fitsColumnWidth) : undefined;

  switch (mode) {
    case 'shrink': {
      const fs = fitsOneColumn(minFontSize) ? largestFontSize(minFontSize, fsBase, fitsOneColumn) : minFontSize;
      return build(fs, 1);
    }
    case 'columns': {
      const fs = twoColumnFont(fsBase);
      return fs === undefined ? build(fsBase, 1) : build(fs, 2);
    }
    case 'truncate':
      return build(fsBase, 1);
    case 'auto':
    default: {
      if (fitsOneColumn(minFontSize)) return build(largestFontSize(minFontSize, fsBase, fitsOneColumn), 1);
      return fitsColumnWidth(minFontSize) ? build(minFontSize, 2) : build(minFontSize, 1);
    }
  }
}

/** Largest size in [min, max] satisfying a predicate monotone in size; `min` when none does. */
function largestFontSize(min: number, max: number, fits: (fs: number) => boolean): number {
  if (max <= min) return min;
  if (fits(max)) return max;
  if (!fits(min)) return min;
  let lo = min;
  let hi = max;
  while (hi - lo > FONT_PRECISION) {
    const mid = (lo + hi) / 2;
    if (fits(mid)) lo = mid;
    else hi = mid;
  }
  return lo;
}

function placeLines(
  lines: readonly GoalScorerLine[],
  box: { width: number; height: number },
  options: GoalScorersLayoutOptions,
  measure: MeasureText,
  fontSize: number,
  columns: 1 | 2,
): GoalScorersLayout {
  const lh = Math.max(...lines.map((l) => measure(l.text, fontSize).height));
  const rowsFit = lh > 0 ? Math.floor(box.height / lh) : 0;
  // The shootout gap can push the last row out: give up lines until it fits.
  let capacity = rowsFit * columns;
  let { visible, hiddenCount } = cut(lines, capacity);
  let stacks = splitColumns(visible, columns);
  while (capacity > 0 && Math.max(...stacks.map((s) => columnHeight(s, lh))) > box.height) {
    capacity -= 1;
    ({ visible, hiddenCount } = cut(lines, capacity));
    stacks = splitColumns(visible, columns);
  }

  const gap = fontSize * GOAL_SCORERS_COLUMN_GAP_EM;
  const colWidth = columns === 2 ? (box.width - gap) / 2 : box.width;
  const contentHeight = Math.max(0, ...stacks.map((s) => columnHeight(s, lh)));
  const top = verticalOffset(options.verticalAlign, box.height, contentHeight);

  const placed = stacks.flatMap((stack, col) => {
    const colX = col * (colWidth + gap);
    let y = top;
    return stack.map((line, row): PlacedGoalScorerLine => {
      if (line.kind === 'shootoutHeader' && row > 0) y += GOAL_SCORERS_SHOOTOUT_GAP * lh;
      const { width, height } = measure(line.text, fontSize);
      const placedLine = { ...line, x: colX + horizontalOffset(options.textAlign, colWidth, width), y, width, height };
      y += lh;
      return placedLine;
    });
  });

  return { fontSize, columns, placed, hiddenCount };
}

/** Two columns: the left one gets ⌈n/2⌉ lines. */
function splitColumns(visible: readonly GoalScorerLine[], columns: 1 | 2): GoalScorerLine[][] {
  if (columns === 1) return [[...visible]];
  const rows = Math.ceil(visible.length / 2);
  return [visible.slice(0, rows), visible.slice(rows)];
}

/** Height of one column's stack: its rows plus the gap above a «Пен.» header inside it. */
function columnHeight(stack: readonly GoalScorerLine[], lh: number): number {
  const headerRow = stack.findIndex((l) => l.kind === 'shootoutHeader');
  return stack.length * lh + (headerRow > 0 ? GOAL_SCORERS_SHOOTOUT_GAP * lh : 0);
}

function cut(lines: readonly GoalScorerLine[], capacity: number): { visible: GoalScorerLine[]; hiddenCount: number } {
  const scorerCount = (list: readonly GoalScorerLine[]) => list.filter((l) => l.kind !== 'shootoutHeader' && l.kind !== 'spacer').length;
  if (lines.length <= capacity) return { visible: [...lines], hiddenCount: 0 };
  if (capacity <= 0) return { visible: [], hiddenCount: scorerCount(lines) };

  const kept = lines.slice(0, capacity - 1);
  while (kept.length > 0 && kept[kept.length - 1].kind === 'shootoutHeader') kept.pop();
  const hiddenCount = scorerCount(lines) - scorerCount(kept);
  return { visible: [...kept, { text: `+${hiddenCount}`, kind: 'more' }], hiddenCount };
}

function verticalOffset(align: PlaceholderVerticalAlign | undefined, boxHeight: number, contentHeight: number): number {
  if (align === 'top') return 0;
  if (align === 'bottom') return boxHeight - contentHeight;
  return (boxHeight - contentHeight) / 2;
}

function horizontalOffset(align: PlaceholderTextAlign, colWidth: number, lineWidth: number): number {
  if (align === 'left') return 0;
  if (align === 'right') return colWidth - lineWidth;
  return (colWidth - lineWidth) / 2;
}

/**
 * Default samples a text placeholder is created with (`editor-left-toolbar.tsx`,
 * `placeholder-plugin.ts`). Picking `goal_scorers` swaps only these — and an
 * empty sample — for `GOAL_SCORERS_SAMPLE`; an author's own text is kept.
 */
const STOCK_TEXT_SAMPLES: ReadonlySet<string> = new Set(['', 'Образец текста', 'Sample text']);

export function isStockTextSample(sample: string | undefined): boolean {
  return STOCK_TEXT_SAMPLES.has((sample ?? '').trim());
}
